import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useNotificationPeriodInDaysErrorText, useRecurringIntervalTypeSelectOptions } from "../AnnualWheel.hooks";
import { RecurringIntervalType } from "../AnnualWheel.types";
import { RelativeDateConfigModel, RelativeDateDirection, RelativeDateReference, RelativeDateUnit } from "../RelativeDate.types";
import { ActivityFormProps, ActivityFrequency } from "./ActivityForm.types";
import { defaultActivityInterval } from "./ActivityScheduleModel";

export type ActivityScheduleSectionProps = Pick<
    ActivityFormProps,
    "model" | "onScheduleChange" | "showError" | "readOnly" | "contractInfo" | "templateRecurringInterval" | "templateRecurringIntervalType"
>;

type RelativeDateField = "startDateRelativeConfig" | "endDateRelativeConfig";

// One sentence per unit, because the languages word the interval differently: Danish counts it as an ordinal
// ("hver 3. måned") and German needs the noun's own plural and capital.
const recurringSummaryTranslationKeys: Record<RecurringIntervalType, string> = {
    [RecurringIntervalType.Week]: "obligationScheduleSummaryRecurringWeeks",
    [RecurringIntervalType.Month]: "obligationScheduleSummaryRecurringMonths",
    [RecurringIntervalType.Year]: "obligationScheduleSummaryRecurringYears",
};

export function useActivityScheduleSection(props: ActivityScheduleSectionProps) {
    const { translateString, translateDate } = useTranslation();
    const intervalTypeOptions = useRecurringIntervalTypeSelectOptions();
    const notificationPeriodInDaysErrorText = useNotificationPeriodInDaysErrorText();
    const model = props.model.schedule;

    const isRecurring = model.isRecurring();
    const frequency = isRecurring ? ActivityFrequency.Recurring : ActivityFrequency.OneTime;
    const frequencyOptions = [
        { value: ActivityFrequency.Recurring, label: translateString("obligationRecurring") },
        { value: ActivityFrequency.OneTime, label: translateString("obligationOneTime") },
    ];

    function onFrequencySelect(selected: ActivityFrequency) {
        if (selected === frequency) {
            return;
        }

        const isOneTime = selected === ActivityFrequency.OneTime;
        props.onScheduleChange({
            interval: isOneTime ? null : (props.templateRecurringInterval ?? defaultActivityInterval),
            recurrenceIntervalType: isOneTime ? undefined : (props.templateRecurringIntervalType ?? RecurringIntervalType.Month),
            endDate: isOneTime ? null : model.endDate,
            displayEndDate: isOneTime ? false : model.displayEndDate,
        });
    }

    function onIntervalChange(interval: string) {
        props.onScheduleChange({ interval: isNullOrWhitespace(interval) ? undefined : Number(interval) });
    }

    function onIntervalTypeChange(intervalType: string | null) {
        props.onScheduleChange({ recurrenceIntervalType: Number(intervalType) });
    }

    function onStartDateChange(date: Date | null) {
        props.onScheduleChange({ startDate: toValidDateOrNull(date) });
    }

    function onEndDateChange(date: Date | null) {
        props.onScheduleChange({ endDate: toValidDateOrNull(date) });
    }

    function onDisplayEndDateChange(displayEndDate: boolean) {
        props.onScheduleChange({ displayEndDate, endDate: displayEndDate ? new Date() : null });
    }

    function onNotificationPeriodInDaysChange(period: string) {
        props.onScheduleChange({ notificationPeriodInDays: isNullOrWhitespace(period) ? undefined : Number(period) });
    }

    function onToggleRelativeStartDate() {
        const useRelativeStartDate = !model.useRelativeStartDate;
        props.onScheduleChange({
            useRelativeStartDate,
            // Closing keeps the date the relative config resolved to, so the schedule does not lose its deadline.
            startDate: useRelativeStartDate ? null : model.calculateAbsoluteStartDate(),
            startDateRelativeConfig: useRelativeStartDate ? createRelativeDateConfig(RelativeDateDirection.Before) : undefined,
        });
    }

    function onToggleRelativeEndDate() {
        const useRelativeEndDate = !model.useRelativeEndDate;
        props.onScheduleChange({
            useRelativeEndDate,
            endDate: useRelativeEndDate ? null : model.calculateAbsoluteEndDate(),
            endDateRelativeConfig: useRelativeEndDate ? createRelativeDateConfig(RelativeDateDirection.After) : undefined,
        });
    }

    function updateRelativeDateConfig(field: RelativeDateField, updates: Partial<RelativeDateConfigModel>) {
        const config = model[field];
        if (config) {
            props.onScheduleChange({ [field]: { ...config, ...updates } });
        }
    }

    function getRelativeDateHandlers(field: RelativeDateField) {
        return {
            onValueChange: (value: string) => updateRelativeDateConfig(field, { value: isNullOrWhitespace(value) ? 0 : Number(value) }),
            onUnitChange: (unit: string) => updateRelativeDateConfig(field, { unit: Number(unit) as RelativeDateUnit }),
            onDirectionChange: (direction: string) => updateRelativeDateConfig(field, { direction: Number(direction) as RelativeDateDirection }),
            onRelativeToChange: (relativeTo: string) => updateRelativeDateConfig(field, { relativeTo: Number(relativeTo) as RelativeDateReference }),
        };
    }

    // Spells out that the activity is a template producing tasks, which is easy to miss when filling in dates.
    function getScheduleSummary() {
        const firstDeadline = model.calculateAbsoluteStartDate();
        if (!firstDeadline) {
            return undefined;
        }

        if (!isRecurring) {
            return translateString("obligationScheduleSummaryOneTime", { date: translateDate(firstDeadline) });
        }

        const { interval, recurrenceIntervalType } = model;
        if (model.isIntervalInvalid() || !interval || recurrenceIntervalType === undefined) {
            return undefined;
        }

        const recurringSummary = translateString(recurringSummaryTranslationKeys[recurrenceIntervalType], {
            count: interval,
            date: translateDate(firstDeadline),
        });
        const lastDeadline = model.displayEndDate ? model.calculateAbsoluteEndDate() : null;

        return lastDeadline
            ? `${recurringSummary} ${translateString("obligationScheduleSummaryEndDate", { date: translateDate(lastDeadline) })}`
            : recurringSummary;
    }

    function getIntervalErrorText() {
        if (!props.showError || !model.isIntervalInvalid()) {
            return undefined;
        }

        return isNullOrWhitespace(model.interval?.toString()) ? translateString("fieldRequired") : translateString("invalidValue");
    }

    return {
        isRecurring,
        canBeOneTime: !props.model.isProcessingActivityValidation,
        frequency,
        frequencyOptions,
        intervalTypeOptions,
        scheduleSummary: getScheduleSummary(),
        intervalErrorText: getIntervalErrorText(),
        showStartDateError: props.showError && model.isStartDateEmpty(),
        showEndDateError: props.showError && model.isEndDateInvalid(),
        notificationPeriodErrorText:
            props.showError && model.isNotificationPeriodInvalid() ? notificationPeriodInDaysErrorText(model.notificationPeriodInDays) : undefined,
        onFrequencySelect,
        onIntervalChange,
        onIntervalTypeChange,
        onStartDateChange,
        onEndDateChange,
        onDisplayEndDateChange,
        onNotificationPeriodInDaysChange,
        onToggleRelativeStartDate,
        onToggleRelativeEndDate,
        startDateRelativeHandlers: getRelativeDateHandlers("startDateRelativeConfig"),
        endDateRelativeHandlers: getRelativeDateHandlers("endDateRelativeConfig"),
    };
}

function toValidDateOrNull(date: Date | null) {
    if (date === null || isNaN(date.getTime())) {
        return null;
    }

    return date;
}

function createRelativeDateConfig(direction: RelativeDateDirection): RelativeDateConfigModel {
    return { value: 1, unit: RelativeDateUnit.Months, direction, relativeTo: RelativeDateReference.ContractStart };
}
